import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Upload as UploadIcon, PhoneOutgoing, Users, User, ChevronLeft,
  Download, History, Trash2, ShieldCheck, X, CheckCircle2, AlertCircle,
  FileSpreadsheet, Zap, Plus,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { useTeam } from '../lib/teamStore';
import { OutboundStore } from '../lib/outboundStore';
import OutboundImportHistoryDrawer from '../components/outbound/OutboundImportHistoryDrawer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const OUTBOUND_API = `${BACKEND_URL}/api/outbound`;

/* ────────────────────────────────────────────────────────────────────
   OUTBOUND IMPORT (full page — /outbound/import)
   Mirrors the MailerUpload aesthetic (title + description on the left,
   Import History + CSV template on the right, field row, dropzone)
   but writes to a *separate* pipeline — outbound leads NEVER touch the
   Mailer collections/dashboards. Assignment supports:
     • Round-robin across every active LO
     • Round-robin across a hand-picked pool
     • Single LO (whole list to one broker)
   Round-robin is exactly balanced (100 rows / 2 LOs = 50 / 50).
   ──────────────────────────────────────────────────────────────── */

// ---- CSV parser (RFC-4180 subset — handles quoted commas + escaped ""s) --
function parseCsv(text) {
  const out = [];
  let row = [], cell = '', i = 0, inQuotes = false;
  while (i < text.length) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { cell += '"'; i += 2; continue; }
        inQuotes = false; i++; continue;
      }
      cell += c; i++; continue;
    }
    if (c === '"') { inQuotes = true; i++; continue; }
    if (c === ',') { row.push(cell); cell = ''; i++; continue; }
    if (c === '\r') { i++; continue; }
    if (c === '\n') { row.push(cell); out.push(row); row = []; cell = ''; i++; continue; }
    cell += c; i++;
  }
  if (cell.length || row.length) { row.push(cell); out.push(row); }
  return out.filter((r) => r.some((v) => (v || '').trim() !== ''));
}

// ---- Canonical outbound field catalog ------------------------------------
// Keys match what the backend's outbound importer accepts (see _COL_ALIASES
// in backend/outbound.py). `phone` is the only truly required field; the
// rest enrich the row but the importer will still take rows without them.
const FIELDS = [
  { key: 'phone',         label: 'Phone',      required: true  },
  { key: 'firstName',     label: 'First Name'  },
  { key: 'lastName',      label: 'Last Name'   },
  { key: 'name',          label: 'Full Name (overrides first+last)' },
  { key: 'email',         label: 'Email'       },
  { key: 'address',       label: 'Address'     },
  { key: 'city',          label: 'City'        },
  { key: 'state',         label: 'State'       },
  { key: 'zip',           label: 'ZIP'         },
  { key: 'purchasePrice', label: 'Purchase Price' },
  { key: 'loanAmount',    label: 'Loan Amount' },
  { key: 'loanDate',      label: 'Loan Date'   },
  { key: 'loanTerm',      label: 'Term (yrs)'  },
  { key: 'loanRate',      label: 'Rate (%)'    },
  { key: 'lender',        label: 'Lender'      },
  { key: 'notes',         label: 'Notes'       },
  { key: 'campaign',      label: 'Campaign (overrides batch)' },
];

const HEADER_ALIASES = {
  phone: 'phone', phone_number: 'phone', phonenumber: 'phone', mobile: 'phone', cell: 'phone', cellphone: 'phone', tel: 'phone', telephone: 'phone',
  first_name: 'firstName', firstname: 'firstName', first: 'firstName', fname: 'firstName', given_name: 'firstName', givenname: 'firstName',
  last_name: 'lastName', lastname: 'lastName', last: 'lastName', lname: 'lastName', surname: 'lastName', family_name: 'lastName', familyname: 'lastName',
  name: 'name', full_name: 'name', fullname: 'name', lead_name: 'name', leadname: 'name',
  email: 'email', email_address: 'email', e_mail: 'email', emailaddress: 'email',
  address: 'address', street: 'address', street_address: 'address', addr: 'address', mailing_address: 'address', property_address: 'address', streetaddress: 'address',
  city: 'city', town: 'city',
  state: 'state', st: 'state', state_code: 'state', region: 'state',
  zip: 'zip', zip_code: 'zip', zipcode: 'zip', postal: 'zip', postal_code: 'zip', postalcode: 'zip',
  loan_amount: 'loanAmount', loanamount: 'loanAmount', amount: 'loanAmount', loan_amt: 'loanAmount', loanamt: 'loanAmount', est_amount: 'loanAmount', estamount: 'loanAmount',
  purchase_price: 'purchasePrice', purchaseprice: 'purchasePrice', sales_price: 'purchasePrice', salesprice: 'purchasePrice', sale_price: 'purchasePrice', saleprice: 'purchasePrice', price: 'purchasePrice', purchase: 'purchasePrice',
  loan_date: 'loanDate', loandate: 'loanDate', date: 'loanDate', funding_date: 'loanDate', close_date: 'loanDate',
  loan_term: 'loanTerm', loanterm: 'loanTerm', term: 'loanTerm', term_years: 'loanTerm', termyears: 'loanTerm', term_yrs: 'loanTerm', termyrs: 'loanTerm',
  loan_rate: 'loanRate', loanrate: 'loanRate', rate: 'loanRate', interest_rate: 'loanRate', interestrate: 'loanRate', int_rate: 'loanRate', intrate: 'loanRate', note_rate: 'loanRate', noterate: 'loanRate', apr: 'loanRate',
  lender: 'lender', bank: 'lender', servicer: 'lender',
  notes: 'notes', note: 'notes', comments: 'notes', comment: 'notes',
  campaign: 'campaign', campaign_name: 'campaign', list: 'campaign', list_name: 'campaign', drop: 'campaign', source: 'campaign',
};

function normalizeHeader(h) {
  const k = (h || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  return HEADER_ALIASES[k] || null;
}

export default function OutboundImport() {
  const navigate = useNavigate();
  const team = useTeam();
  const fileRef = useRef(null);

  const [file, setFile] = useState(null);
  const [rowPreview, setRowPreview] = useState(0);
  const [grid, setGrid] = useState(null);        // [[headers], [row1], ...] — populated after CSV parse
  const [mapping, setMapping] = useState({});    // { csvHeader: 'canonicalKey' | null }
  const [wantedFields, setWantedFields] = useState([]); // canonical keys queued to be mapped
  const [pickAdd, setPickAdd] = useState('');
  const [campaign, setCampaign] = useState('');
  const [assignmentMode, setAssignmentMode] = useState('round_robin'); // 'round_robin' | 'single'
  const [singleLo, setSingleLo] = useState('');
  const [poolIds, setPoolIds] = useState(() => new Set()); // empty → all active LOs
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const activeLos = useMemo(
    () => (team || []).filter((m) => {
      if (m.active === false || m.is_active === false) return false;
      // LOs always eligible. Admins ONLY when flagged in as dialer-
      // eligible (currently Andrew, Cody, Scott).
      if (m.role === 'admin') return !!m.dialer_eligible;
      return true;
    }),
    [team],
  );

  // Pool preview — resolves to the LOs that will actually receive rows.
  const effectivePool = useMemo(() => {
    if (assignmentMode === 'single') {
      return singleLo ? activeLos.filter((m) => m.id === singleLo) : [];
    }
    if (poolIds.size === 0) return activeLos;
    return activeLos.filter((m) => poolIds.has(m.id));
  }, [assignmentMode, singleLo, poolIds, activeLos]);

  // Per-LO preview (balanced round-robin math the server also runs).
  const perLoPreview = useMemo(() => {
    if (!rowPreview || effectivePool.length === 0) return {};
    const each = Math.floor(rowPreview / effectivePool.length);
    const remainder = rowPreview % effectivePool.length;
    const out = {};
    effectivePool.forEach((lo, i) => { out[lo.id] = each + (i < remainder ? 1 : 0); });
    return out;
  }, [rowPreview, effectivePool]);

  const togglePool = (id) => {
    setPoolIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAllPool = () => setPoolIds(new Set(activeLos.map((m) => m.id)));
  const clearPool = () => setPoolIds(new Set());

  // Mapping-derived state ────────────────────────────────────────────
  const mappedFields = useMemo(
    () => new Set(Object.values(mapping).filter(Boolean)),
    [mapping],
  );
  const unmappedHeaders = useMemo(
    () => (grid ? grid[0].filter((h) => !mapping[h]) : []),
    [grid, mapping],
  );
  const availableForPick = useMemo(
    () => FIELDS.filter((f) => !mappedFields.has(f.key) && !wantedFields.includes(f.key)),
    [mappedFields, wantedFields],
  );
  // Backend requires phone. Names improve UX but aren't hard-required
  // (fallback to phone). We only block on phone here.
  const missingRequired = useMemo(() => ({
    hasPhone: mappedFields.has('phone'),
    hasName:  mappedFields.has('name') || mappedFields.has('firstName') || mappedFields.has('lastName'),
  }), [mappedFields]);

  // Preview rows — up to 200 mapped rows for the on-page table.
  const previewRows = useMemo(() => {
    if (!grid || grid.length < 2) return [];
    const headers = grid[0];
    return grid.slice(1, 201).map((cells) => {
      const obj = {};
      headers.forEach((h, i) => {
        const canon = mapping[h];
        if (!canon) return;
        const v = (cells[i] ?? '').toString().trim();
        if (v) obj[canon] = v;
      });
      return obj;
    });
  }, [grid, mapping]);

  const remap = (header, value) => {
    setMapping((prev) => {
      const next = { ...prev };
      if (value) {
        for (const k of Object.keys(next)) {
          if (next[k] === value && k !== header) next[k] = null;
        }
      }
      next[header] = value || null;
      return next;
    });
  };

  const addWantedField = () => {
    if (!pickAdd) return;
    if (mappedFields.has(pickAdd)) {
      toast.info(`${FIELDS.find((f) => f.key === pickAdd)?.label} is already mapped`);
      setPickAdd('');
      return;
    }
    setWantedFields((prev) => (prev.includes(pickAdd) ? prev : [...prev, pickAdd]));
    setPickAdd('');
  };

  const removeWantedField = (key) => {
    setWantedFields((prev) => prev.filter((k) => k !== key));
  };

  const handleFile = useCallback(async (f) => {
    if (!f) return;
    if (!/\.csv$|text\/csv/i.test(f.name + ' ' + f.type)) {
      toast.error('Please pick a .csv file');
      return;
    }
    try {
      const txt = await f.text();
      const parsed = parseCsv(txt);
      if (parsed.length < 2) {
        toast.error('CSV has no data rows');
        return;
      }
      const headers = parsed[0];
      // Build initial auto-mapping — never map two headers to the same field.
      const seen = new Set();
      const initial = {};
      for (const h of headers) {
        const canon = normalizeHeader(h);
        if (canon && !seen.has(canon)) {
          initial[h] = canon;
          seen.add(canon);
        } else {
          initial[h] = null;
        }
      }
      setFile(f);
      setGrid(parsed);
      setMapping(initial);
      setRowPreview(parsed.length - 1);
      setResult(null);
      const autoCount = Object.values(initial).filter(Boolean).length;
      toast.success(`Parsed ${parsed.length - 1} rows · auto-mapped ${autoCount} of ${headers.length} columns`);
    } catch (e) {
      toast.error('Could not read file: ' + (e?.message || 'unknown error'));
    }
  }, []);

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  const submit = async () => {
    if (!file) return toast.error('Pick a CSV file first');
    if (!campaign.trim()) return toast.error('Give this list a campaign name');
    if (!missingRequired.hasPhone) return toast.error('Map a Phone column — required by the outbound dialer');
    if (assignmentMode === 'single' && !singleLo) return toast.error('Pick a loan officer');
    if (effectivePool.length === 0) return toast.error('Round-robin pool is empty — pick at least one active LO');
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('campaign', campaign.trim());
      // Send the admin's column mapping as JSON so the backend uses it
      // verbatim instead of the alias auto-picker.
      const clean = Object.fromEntries(Object.entries(mapping).filter(([, v]) => v));
      fd.append('mapping', JSON.stringify(clean));
      if (assignmentMode === 'single' && singleLo) {
        fd.append('assign_to', singleLo);
      } else if (assignmentMode === 'round_robin' && poolIds.size > 0) {
        // Only send pool_ids when the admin trimmed the default. An
        // empty set means "everyone active" — same behavior as before.
        fd.append('pool_ids', Array.from(poolIds).join(','));
      }
      const r = await axios.post(`${OUTBOUND_API}/import`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(r.data);
      toast.success(`Imported ${r.data.inserted} lead${r.data.inserted === 1 ? '' : 's'}`);
      // Warm the Outbound cache so the freshly imported rows show up
      // instantly when the admin navigates to /outbound next.
      OutboundStore.refresh();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Import failed';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setFile(null);
    setGrid(null);
    setMapping({});
    setWantedFields([]);
    setPickAdd('');
    setRowPreview(0);
    setCampaign('');
    setSingleLo('');
    setPoolIds(new Set());
    setResult(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const loName = (id) => activeLos.find((m) => m.id === id)?.name || id;

  return (
    <div data-testid="outbound-import-page" className="pb-12">
      {/* Back link */}
      <button
        onClick={() => navigate('/outbound')}
        className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-neutral-500 hover:text-neutral-900 mb-3"
      >
        <ChevronLeft className="h-3.5 w-3.5" /> Back to Outbound
      </button>

      {/* Header — mirrors MailerUpload's header layout */}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.24em] font-bold text-neutral-500">
            <ShieldCheck className="h-3 w-3 text-[hsl(var(--kf-gold-deep))]" /> Admin · Outbound
          </div>
          <h1 className="mt-1 text-3xl font-black tracking-tight text-neutral-900">Import Outbound Leads</h1>
          <p className="mt-1 text-[13px] text-neutral-500 max-w-2xl">
            Drop a cold-call list. Rows land as <b className="text-neutral-800">outbound-only</b> leads
            — they stay isolated from My Leads / All Leads and the Mailer reporting. Assign to a single
            LO or spread evenly across a hand-picked round-robin pool.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="outbound-import-history-btn"
            onClick={() => setHistoryOpen(true)}
            className="h-10 px-3.5 rounded-lg border hairline bg-white hover:bg-neutral-50 text-[12.5px] font-semibold text-neutral-700 flex items-center gap-1.5"
          >
            <History className="h-3.5 w-3.5" /> Import history
          </button>
          <a
            data-testid="outbound-import-template-btn"
            href={`${OUTBOUND_API}/template.csv`}
            className="h-10 px-3.5 rounded-lg border hairline bg-white hover:bg-neutral-50 text-[12.5px] font-semibold text-neutral-700 flex items-center gap-1.5"
          >
            <Download className="h-3.5 w-3.5" /> CSV template
          </a>
        </div>
      </div>

      {result ? (
        <ImportSummaryCard result={result} loName={loName} onReset={reset} onDone={() => navigate('/outbound')} />
      ) : (
        <div className="space-y-4">
          {/* Field row — mirrors Mailer's 3-column header row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 rounded-2xl border hairline bg-white px-5 py-4">
            <Field label="Campaign name" required>
              <input
                data-testid="outbound-import-campaign"
                type="text"
                value={campaign}
                onChange={(e) => setCampaign(e.target.value)}
                placeholder="e.g. Refi Tour · Sept Wave A"
                className="w-full h-11 px-3 rounded-md bg-white border hairline text-[14px] font-semibold text-neutral-900 placeholder:font-normal placeholder:text-neutral-400 focus:outline-none focus:border-neutral-900 transition"
                required
              />
              <FieldHint>Tags every row with this campaign for outbound reporting.</FieldHint>
            </Field>

            <Field label="Assignment">
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                <ModeTile
                  active={assignmentMode === 'round_robin'}
                  onClick={() => setAssignmentMode('round_robin')}
                  icon={Users}
                  title="Round-robin"
                  desc="Even split across pool"
                  testId="outbound-mode-round-robin"
                />
                <ModeTile
                  active={assignmentMode === 'single'}
                  onClick={() => setAssignmentMode('single')}
                  icon={User}
                  title="Single LO"
                  desc="All rows to one broker"
                  testId="outbound-mode-single"
                />
              </div>
            </Field>

            <Field label={assignmentMode === 'single' ? 'Loan officer' : 'Pool preview'}>
              {assignmentMode === 'single' ? (
                <select
                  data-testid="outbound-single-lo"
                  value={singleLo}
                  onChange={(e) => setSingleLo(e.target.value)}
                  className="w-full h-11 px-3 rounded-md bg-white border hairline text-[13px] font-semibold text-neutral-900 focus:outline-none focus:border-neutral-900"
                >
                  <option value="">— Pick a loan officer —</option>
                  {activeLos.map((lo) => <option key={lo.id} value={lo.id}>{lo.name}</option>)}
                </select>
              ) : (
                <div className="mt-1.5 flex items-center gap-2 h-11 px-3 rounded-md border hairline bg-neutral-50">
                  <span className="h-6 w-6 rounded-full bg-[hsl(var(--kf-gold))] text-neutral-950 text-[11px] font-black flex items-center justify-center">{effectivePool.length}</span>
                  <span className="text-[12.5px] font-semibold text-neutral-700 truncate">
                    {effectivePool.length === activeLos.length && poolIds.size === 0
                      ? 'All active LOs' : `${effectivePool.length} selected`}
                  </span>
                </div>
              )}
              <FieldHint>
                {assignmentMode === 'single'
                  ? 'The entire list goes to this LO.'
                  : 'Rows are divided evenly — 100 rows / 2 LOs = 50 each.'}
              </FieldHint>
            </Field>
          </div>

          {/* Pool selector — only for round-robin */}
          {assignmentMode === 'round_robin' && (
            <div className="rounded-2xl border hairline bg-white px-5 py-4" data-testid="outbound-pool-selector">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <div>
                  <div className="text-[12.5px] font-bold text-neutral-900">Round-robin pool</div>
                  <div className="text-[11px] text-neutral-500">
                    {poolIds.size === 0
                      ? <>Empty = <b>every active LO</b> ({activeLos.length}) rotates.</>
                      : <>Only the <b>{poolIds.size}</b> loan officer{poolIds.size === 1 ? '' : 's'} you pick will receive rows.</>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    data-testid="outbound-pool-all"
                    onClick={selectAllPool}
                    className="h-8 px-3 rounded-md border hairline bg-white hover:bg-neutral-50 text-[11.5px] font-semibold text-neutral-700"
                  >Select all</button>
                  <button
                    data-testid="outbound-pool-clear"
                    onClick={clearPool}
                    className="h-8 px-3 rounded-md border hairline bg-white hover:bg-neutral-50 text-[11.5px] font-semibold text-neutral-500"
                  >Clear</button>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {activeLos.map((lo) => {
                  const picked = poolIds.has(lo.id);
                  // Only show the per-LO share when the pool has picks
                  // OR is empty (empty = fallback "all LOs" mode). Either
                  // way we honor `effectivePool` for the number.
                  const inEffectivePool = poolIds.size === 0 || picked;
                  const share = inEffectivePool ? perLoPreview[lo.id] : undefined;
                  return (
                    <button
                      key={lo.id}
                      data-testid={`outbound-pool-lo-${lo.id}`}
                      onClick={() => togglePool(lo.id)}
                      className={cn(
                        'flex items-center gap-2.5 px-3 py-2.5 rounded-lg border text-left transition',
                        picked
                          ? 'border-[hsl(var(--kf-gold))]/40 bg-[hsl(var(--kf-gold-soft))]/40 hover:bg-[hsl(var(--kf-gold-soft))]/60'
                          : 'border-neutral-200 bg-white hover:bg-neutral-50'
                      )}
                    >
                      <span className="h-8 w-8 shrink-0 rounded-full bg-neutral-900 text-white text-[10.5px] font-black flex items-center justify-center">
                        {lo.initials || lo.name?.split(' ').map((s) => s[0]).join('').slice(0, 2).toUpperCase() || '?'}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-[12.5px] font-bold text-neutral-900 truncate">{lo.name}</div>
                        <div className="text-[10.5px] text-neutral-500 truncate">{lo.role_label || lo.title || 'Loan Officer'}</div>
                      </div>
                      {share ? (
                        <span className="shrink-0 text-[10.5px] tabular-nums font-black rounded-md bg-neutral-900 text-white px-1.5 py-0.5">{share}</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Dropzone or file card */}
          {!file ? (
            <div
              data-testid="outbound-dropzone"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => fileRef.current?.click()}
              className={cn(
                'cursor-pointer rounded-2xl border-2 border-dashed transition flex flex-col items-center justify-center py-16 text-center px-6',
                dragOver ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-300 hover:border-neutral-500 bg-white'
              )}
            >
              <div className="h-12 w-12 rounded-full bg-neutral-900 text-[hsl(var(--kf-gold))] flex items-center justify-center mb-3">
                <UploadIcon className="h-5 w-5" />
              </div>
              <div className="text-[20px] font-bold text-neutral-900 tracking-tight">Drop CSV or click to upload</div>
              <div className="text-[12.5px] text-neutral-500 mt-1 max-w-xl">
                Required column: <span className="mono">phone</span>. Optional: <span className="mono">firstName, lastName, name, email, city, state, zip, address, notes, campaign</span>.
              </div>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                hidden
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
              />
            </div>
          ) : (
            <div className="rounded-2xl border hairline bg-white overflow-hidden">
              <div className="px-5 py-4 border-b hairline bg-neutral-50/40 flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="h-5 w-5 text-[hsl(var(--kf-gold-deep))]" />
                  <div>
                    <div className="text-[13.5px] font-bold text-neutral-900">{file.name}</div>
                    <div className="text-[11.5px] text-neutral-500 mono">
                      <span className="font-bold text-neutral-700">{rowPreview.toLocaleString()}</span> rows ·{' '}
                      <span className="font-bold text-neutral-700">{mappedFields.size}</span> mapped fields ·{' '}
                      <span className={unmappedHeaders.length ? 'text-amber-700' : ''}>{unmappedHeaders.length} ignored</span> · split across{' '}
                      <span className="font-bold text-neutral-700">{effectivePool.length}</span> loan officer{effectivePool.length === 1 ? '' : 's'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={reset}
                    data-testid="outbound-discard-btn"
                    className="h-9 px-3 rounded-md border hairline bg-white hover:bg-neutral-50 text-[12px] font-semibold text-neutral-600 flex items-center gap-1.5"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Discard
                  </button>
                  <button
                    onClick={submit}
                    data-testid="outbound-import-submit"
                    disabled={busy || !campaign.trim() || !missingRequired.hasPhone}
                    title={!missingRequired.hasPhone ? 'Map a Phone column first' : ''}
                    className="h-9 px-4 rounded-md bg-neutral-900 text-white text-[12.5px] font-bold flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <UploadIcon className="h-4 w-4" /> {busy ? 'Uploading…' : `Import ${rowPreview.toLocaleString()} leads`}
                  </button>
                </div>
              </div>

              {/* Requirement banner — mirrors the mailer's amber gate */}
              {!missingRequired.hasPhone && (
                <div className="px-5 py-2.5 bg-amber-50 border-b border-amber-100 text-[11.5px] text-amber-800 flex items-start gap-2">
                  <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <div>
                    Missing required mapping: <span className="mono font-semibold">Phone</span> — the dialer can't call a row without it.
                  </div>
                </div>
              )}

              {/* Column Mapping — matches the mailer dashboard exactly */}
              <div className="p-5 border-b hairline" data-testid="outbound-mapping">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div className="text-[10.5px] uppercase tracking-[0.2em] text-neutral-400 font-bold">Column Mapping</div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="text-[11px] text-neutral-500">
                      <span className="inline-flex items-center gap-1 mr-3"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> auto-matched</span>
                      <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-amber-400" /> needs mapping</span>
                    </div>
                    {availableForPick.length > 0 && (
                      <div className="flex items-center gap-1.5" data-testid="outbound-add-field-picker">
                        <select
                          value={pickAdd}
                          onChange={(e) => setPickAdd(e.target.value)}
                          data-testid="outbound-add-field-select"
                          className="h-8 px-2 rounded-md border hairline bg-white text-[12px] font-semibold text-neutral-900 focus:outline-none focus:border-neutral-900"
                        >
                          <option value="">Add a field…</option>
                          {availableForPick.map((f) => (
                            <option key={f.key} value={f.key}>{f.label}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={addWantedField}
                          disabled={!pickAdd}
                          data-testid="outbound-add-field-confirm"
                          className="h-8 px-2.5 rounded-md bg-neutral-900 text-white text-[11.5px] font-bold inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Plus className="h-3.5 w-3.5" /> Add
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                {wantedFields.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-1.5 items-center" data-testid="outbound-wanted-field-chips">
                    <span className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-neutral-400 mr-1">To map:</span>
                    {wantedFields.map((key) => {
                      const f = FIELDS.find((x) => x.key === key);
                      if (!f) return null;
                      const isMapped = mappedFields.has(key);
                      return (
                        <span
                          key={key}
                          className={cn(
                            'inline-flex items-center gap-1 h-6 px-2 rounded-full border text-[10.5px] font-bold uppercase tracking-wider',
                            isMapped
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : 'bg-[hsl(var(--kf-gold-soft))] text-[hsl(var(--kf-gold-deep))] border-[hsl(var(--kf-gold))]/40',
                          )}
                          data-testid={`outbound-wanted-chip-${key}`}
                        >
                          {isMapped && <CheckCircle2 className="h-3 w-3" />}
                          {f.label}
                          <button
                            onClick={() => removeWantedField(key)}
                            className="h-3.5 w-3.5 rounded-full hover:bg-black/10 flex items-center justify-center"
                            aria-label={`Remove ${f.label}`}
                            data-testid={`outbound-wanted-remove-${key}`}
                          >
                            <X className="h-2.5 w-2.5" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                  {grid[0].map((header) => {
                    const value = mapping[header] || '';
                    const isAuto = !!value;
                    return (
                      <div
                        key={header}
                        className="flex items-center gap-3 py-2 border-b border-neutral-100 last:border-b-0"
                        data-testid={`outbound-mapping-row-${header}`}
                      >
                        <span
                          className={cn(
                            'h-1.5 w-1.5 rounded-full shrink-0',
                            isAuto ? 'bg-emerald-500' : 'bg-amber-400',
                          )}
                          aria-hidden
                        />
                        <span
                          className={cn(
                            'text-[12.5px] font-semibold mono truncate w-[140px] shrink-0',
                            isAuto ? 'text-neutral-700' : 'text-amber-800',
                          )}
                          title={header}
                        >
                          {header}
                        </span>
                        <span className="text-neutral-300 shrink-0" aria-hidden>→</span>
                        <select
                          data-testid={`outbound-mapping-select-${header}`}
                          value={value}
                          onChange={(e) => remap(header, e.target.value)}
                          className={cn(
                            'flex-1 h-9 px-2.5 rounded-md bg-white border hairline text-[12.5px] font-semibold focus:outline-none focus:border-neutral-900 transition',
                            !value && 'text-amber-700 italic',
                          )}
                        >
                          <option value="">Ignore this column</option>
                          {FIELDS.map((f) => {
                            const used = mappedFields.has(f.key) && mapping[header] !== f.key;
                            return (
                              <option key={f.key} value={f.key} disabled={used}>
                                {f.label}{used ? ' (already used)' : ''}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Preview · first 200 mapped rows */}
              {previewRows.length > 0 && (
                <div>
                  <div className="px-5 py-3 border-b hairline flex items-center justify-between bg-neutral-50/40">
                    <div className="text-[10.5px] uppercase tracking-[0.2em] text-neutral-400 font-bold">Preview · first 200 rows</div>
                    <div className="text-[11px] text-neutral-500">
                      All {rowPreview.toLocaleString()} rows will be uploaded on submit
                    </div>
                  </div>
                  <OutboundPreviewTable rows={previewRows} mappedFields={mappedFields} />
                </div>
              )}

              {/* Split-preview strip */}
              {rowPreview > 0 && effectivePool.length > 0 && (
                <div className="px-5 py-4 border-t hairline">
                  <div className="text-[10.5px] uppercase tracking-wider font-bold text-neutral-500 mb-2 flex items-center gap-1.5">
                    <Zap className="h-3 w-3" /> Preview split
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                    {effectivePool.map((lo) => (
                      <div key={lo.id} className="flex items-center justify-between rounded-lg border hairline bg-neutral-50 px-3 py-2">
                        <div className="min-w-0">
                          <div className="text-[12px] font-bold text-neutral-900 truncate">{lo.name}</div>
                          <div className="text-[10.5px] text-neutral-500 truncate">{lo.role_label || 'Loan Officer'}</div>
                        </div>
                        <div className="shrink-0 text-[14px] font-black tabular-nums text-neutral-900">
                          {perLoPreview[lo.id] || 0}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <OutboundImportHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        onReverted={() => OutboundStore.refresh()}
      />
    </div>
  );
}

/* ── Result card ─────────────────────────────────────────────────── */
function ImportSummaryCard({ result, loName, onReset, onDone }) {
  const perLo = result.per_lo_counts || {};
  const totalLos = Object.keys(perLo).length;
  return (
    <div data-testid="outbound-import-result" className="rounded-2xl border hairline bg-white overflow-hidden">
      <div className="px-6 py-5 border-b hairline bg-emerald-50/40 flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-emerald-600 text-white flex items-center justify-center">
          <CheckCircle2 className="h-5 w-5" />
        </div>
        <div>
          <div className="text-[15px] font-black text-neutral-900">Import complete</div>
          <div className="text-[12px] text-neutral-600">
            <b>{result.campaign}</b> · {result.inserted.toLocaleString()} lead{result.inserted === 1 ? '' : 's'} · split across {totalLos} LO{totalLos === 1 ? '' : 's'}
          </div>
        </div>
      </div>
      <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SummaryStat label="Rows in file" value={result.row_count} />
        <SummaryStat label="Inserted"     value={result.inserted} tone="ok" />
        <SummaryStat label="Skipped"      value={result.skipped}  tone={result.skipped ? 'warn' : 'muted'} />
      </div>
      {Object.keys(perLo).length > 0 && (
        <div className="px-6 pb-6">
          <div className="text-[10.5px] uppercase tracking-wider font-bold text-neutral-500 mb-2">Distribution</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {Object.entries(perLo).sort((a, b) => b[1] - a[1]).map(([id, n]) => (
              <div key={id} className="flex items-center justify-between rounded-lg border hairline bg-neutral-50 px-3 py-2">
                <span className="text-[12.5px] font-semibold text-neutral-900 truncate">{loName(id)}</span>
                <span className="text-[13px] font-black tabular-nums text-neutral-900">{n}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="px-6 py-4 border-t hairline flex justify-end gap-2 bg-neutral-50/40">
        <button
          onClick={onReset}
          className="h-9 px-4 rounded-md border hairline bg-white hover:bg-neutral-50 text-[12.5px] font-semibold text-neutral-700"
        >Import another list</button>
        <button
          onClick={onDone}
          data-testid="outbound-import-done"
          className="h-9 px-4 rounded-md bg-neutral-900 text-white text-[12.5px] font-bold"
        >Go to Outbound</button>
      </div>
    </div>
  );
}

/* ── Field wrappers ──────────────────────────────────────────────── */
function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-neutral-500">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
    </div>
  );
}
function FieldHint({ children }) {
  return <div className="text-[11px] text-neutral-500 mt-1">{children}</div>;
}
function ModeTile({ active, onClick, icon: Icon, title, desc, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={cn(
        'flex flex-col items-start gap-0.5 rounded-lg border px-3 py-2 h-11 justify-center transition text-left',
        active ? 'border-neutral-900 bg-neutral-900 text-white' : 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50'
      )}
    >
      <div className="flex items-center gap-1.5 text-[11.5px] font-bold">
        <Icon className="h-3 w-3" /> {title}
      </div>
      <div className={cn('text-[10px]', active ? 'text-white/60' : 'text-neutral-500')}>{desc}</div>
    </button>
  );
}
function SummaryStat({ label, value, tone = 'muted' }) {
  const toneCls = { ok: 'text-emerald-600', warn: 'text-amber-600', muted: 'text-neutral-900' }[tone];
  return (
    <div>
      <div className="text-[9.5px] uppercase tracking-wider font-bold text-neutral-500">{label}</div>
      <div className={cn('text-3xl font-black tabular-nums', toneCls)}>{Number(value || 0).toLocaleString()}</div>
    </div>
  );
}

/* ── Preview table — only renders columns the admin actually mapped ─── */
function OutboundPreviewTable({ rows, mappedFields }) {
  const COLUMN_DEFS = useMemo(() => ({
    phone:      { label: 'Phone',   render: (r) => <span className="mono text-neutral-800 font-semibold">{r.phone}</span> },
    firstName:  { label: 'First',   render: (r) => <span className="text-neutral-900 font-semibold">{r.firstName}</span> },
    lastName:   { label: 'Last',    render: (r) => <span className="text-neutral-900 font-semibold">{r.lastName}</span> },
    name:       { label: 'Name',    render: (r) => <span className="font-semibold text-neutral-900">{r.name}</span> },
    email:      { label: 'Email',   render: (r) => <span className="text-neutral-600 truncate inline-block max-w-[180px] align-middle">{r.email}</span> },
    address:    { label: 'Address', render: (r) => <span className="text-neutral-700 truncate inline-block max-w-[200px] align-middle">{r.address}</span> },
    city:       { label: 'City',    render: (r) => <span className="text-neutral-700">{r.city}</span> },
    state:      { label: 'ST',      render: (r) => <span className="mono text-neutral-700">{r.state}</span> },
    zip:        { label: 'ZIP',     render: (r) => <span className="mono text-neutral-700">{r.zip}</span> },
    loanAmount: { label: 'Loan Amt', align: 'right', render: (r) => {
      const n = Number(String(r.loanAmount).replace(/[^0-9.\-]/g, ''));
      return <span className="tabular-nums font-semibold text-neutral-900">{Number.isFinite(n) && n > 0 ? `$${n.toLocaleString('en-US')}` : r.loanAmount}</span>;
    } },
    purchasePrice: { label: 'Purchase Price', align: 'right', render: (r) => {
      const n = Number(String(r.purchasePrice).replace(/[^0-9.\-]/g, ''));
      return <span className="tabular-nums font-semibold text-neutral-900">{Number.isFinite(n) && n > 0 ? `$${n.toLocaleString('en-US')}` : r.purchasePrice}</span>;
    } },
    loanDate:   { label: 'Loan Date', render: (r) => <span className="tabular-nums text-neutral-700">{r.loanDate}</span> },
    loanTerm:   { label: 'Term', align: 'right', render: (r) => {
      const raw = String(r.loanTerm || '').match(/\d+/)?.[0];
      const n = raw ? parseInt(raw, 10) : null;
      return <span className="tabular-nums text-neutral-700">{n ? `${n} yr` : (r.loanTerm || '')}</span>;
    } },
    loanRate:   { label: 'Rate', align: 'right', render: (r) => {
      const raw = String(r.loanRate || '').replace(/[^0-9.\-]/g, '');
      const n = raw ? parseFloat(raw) : null;
      if (!Number.isFinite(n) || n <= 0) return <span className="text-neutral-500">{r.loanRate || ''}</span>;
      const pct = n < 1 ? n * 100 : n;
      return <span className="tabular-nums font-semibold text-neutral-900">{parseFloat(pct.toFixed(3))}%</span>;
    } },
    lender:     { label: 'Lender',    render: (r) => <span className="text-neutral-700 truncate inline-block max-w-[180px] align-middle">{r.lender}</span> },
    notes:      { label: 'Notes',   render: (r) => <span className="text-neutral-600 truncate inline-block max-w-[220px] align-middle">{r.notes}</span> },
    campaign:   { label: 'Campaign', render: (r) => <span className="text-neutral-700">{r.campaign}</span> },
  }), []);

  const activeColumns = useMemo(() => {
    const list = [];
    for (const f of FIELDS) {
      if (mappedFields.has(f.key) && COLUMN_DEFS[f.key]) {
        list.push({ key: f.key, ...COLUMN_DEFS[f.key] });
      }
    }
    return list;
  }, [mappedFields, COLUMN_DEFS]);

  if (activeColumns.length === 0) {
    return (
      <div className="p-10 text-center text-[13px] text-neutral-500">
        Map at least one column above to preview your data.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto max-h-[420px]">
      <table className="w-full text-[12px]" data-testid="outbound-preview-table">
        <thead className="bg-white border-b hairline sticky top-0 z-10 shadow-[0_1px_0_hsl(0_0%_92%)]">
          <tr className="text-left text-[10px] uppercase tracking-wider text-neutral-500 font-bold">
            <th className="px-4 py-2.5 w-10 bg-white">#</th>
            {activeColumns.map((col) => (
              <th
                key={col.key}
                className={cn(
                  'px-4 py-2.5 whitespace-nowrap bg-white',
                  col.align === 'right' && 'text-right',
                )}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100">
          {rows.map((r, i) => (
            <tr key={i} className="hover:bg-neutral-50/50" data-testid={`outbound-preview-row-${i}`}>
              <td className="px-4 py-2.5 mono text-neutral-400 text-[10.5px]">{i + 1}</td>
              {activeColumns.map((col) => {
                const v = r[col.key];
                const empty = v == null || v === '';
                return (
                  <td
                    key={col.key}
                    className={cn(
                      'px-4 py-2.5 align-middle whitespace-nowrap',
                      col.align === 'right' && 'text-right',
                    )}
                  >
                    {empty ? <span className="text-neutral-300">—</span> : col.render(r)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
